import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AppRequestContextService } from './app-request-context.service';
import { StockService } from '@pedix-workspace/pedixapp-stock';
import { AppCartItemsService } from './app-cart-items.service';
import { EstablishmentsStockService } from './establishments-stock.service';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { isSubscriptionActive } from '@pedix-workspace/utils';
import { distinctUntilChanged, from, map, of, switchMap, tap } from 'rxjs';
import { ApiV1_ProductStock } from '@pedix-workspace/api-v1';
import { EstablishmentsCheckoutService } from './features/order-checkout/establishments-checkout.service';

@Injectable({
  providedIn: 'root',
})
export class AppEstablishmentGuardService {
  private platformId = inject(PLATFORM_ID);

  private router = inject(Router);
  private appRequestContextService = inject(AppRequestContextService);
  private appCartItemsService = inject(AppCartItemsService);
  private stockService = inject(StockService);
  private establishmentsStockService = inject(EstablishmentsStockService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);

  constructor() {
    // Syncs cartItems to stockService
    toObservable(this.appCartItemsService.cartItems).subscribe(cartItems => {
      this.stockService.cartItems.set(cartItems);
    });
    toObservable(this.appRequestContextService.allProducts)
      .pipe(distinctUntilChanged())
      .subscribe(products => {
        this.appCartItemsService.syncPurchaseRestrictions(products || []);
      });
    // Syncs stockCheckDisabled to stockService
    toObservable(this.appRequestContextService.stockCheckDisabled).subscribe(stockCheckDisabled => {
      this.stockService.stockCheckDisabled.set(stockCheckDisabled);
    });
    // Syncs productStock (from the /stock API) to stockService, per establishment
    toObservable(this.appRequestContextService.establishment)
      .pipe(
        map(establishment => establishment?.id),
        distinctUntilChanged(),
        tap(establishmentId => {
          if (establishmentId) {
            this.establishmentsCheckoutService.get(establishmentId).catch(() => null);
          }
        }),
        switchMap(establishmentId =>
          establishmentId
            ? from(this.establishmentsStockService.get(establishmentId))
            : of<ApiV1_ProductStock[]>([]),
        ),
      )
      // null = fetch failed → keep the last known stock instead of wiping it to [] (all-infinite).
      .subscribe(productStock => {
        if (productStock !== null) {
          this.stockService.productStock.set(productStock);
        }
      });
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const { previewMode } = route.queryParams;

    if (!this.appRequestContextService.establishment()) {
      this.router.navigate(['/not-found'], { skipLocationChange: true });

      return false;
    }

    const establishment = this.appRequestContextService.establishment();

    // NOTE: esto no se puede hacer en el server?
    if (
      this.appRequestContextService.source() === 'subdomain' &&
      establishment.subdomainStatus !== 'ACTIVE'
    ) {
      this.router.navigate(['/not-found'], { skipLocationChange: true });

      return false;
    }

    if (!isSubscriptionActive(establishment.subscription)) {
      this.router.navigate(['/payment-issue'], { skipLocationChange: true });
      return false;
    }

    this.appRequestContextService.previewMode.set(Boolean(previewMode));

    // Updates cartItems data
    if (isPlatformBrowser(this.platformId)) {
      this.appCartItemsService.initSession(this.appRequestContextService.slug());
    }

    return true;
  }
}
